import { useForm } from 'react-hook-form';
import type { ChangePasswordFormValues } from './types';
import { usePasswordChecks } from '@/shared/lib';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { createChangePasswordFormSchema } from './changePasswordSchema';

const useChangePasswordForm = () => {
  const { t } = useTranslation();

  const schema = createChangePasswordFormSchema(t);

  const form = useForm<ChangePasswordFormValues>({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: {
      password: '',
      twoFA: {
        state: '',
        identityType: 'phone',
        code: '',
      },
    },
  });

  const password = form.watch('password');

  const {
    unmetPasswordChecks: unmetPasswordChecksAfterSubmit,
    passwordChecks,
  } = usePasswordChecks(form);

  const unmetPasswordChecksBeforeSubmit = useMemo(() => {
    if (!password) return [];

    const validation = createChangePasswordFormSchema(t).safeParse({
      password,
    });

    if (validation.success) {
      return [];
    }

    const errorMessages = validation.error.issues.map(issue => issue.message);

    return errorMessages;
  }, [password, t]);

  const resetCode = () => {
    form.setValue('twoFA.code', '');
  };

  return {
    form,
    unmetPasswordChecksBeforeSubmit,
    unmetPasswordChecksAfterSubmit,
    passwordChecks,
    resetCode,
  };
};

export { useChangePasswordForm };
